import { Controller } from "@hotwired/stimulus";
import React from 'react';
import { createRoot } from 'react-dom/client';
import JsonSchemaForm from '../javascript/components/JsonSchemaForm.jsx';
import { HiddenFormBuilder } from '../javascript/utils/HiddenFormBuilder.js';
import { DefaultUiSchemaBuilder } from '../javascript/utils/DefaultUiSchemaBuilder.js';

export default class extends Controller {
  static values = {
    schema: Object,
    formData: { type: Object, default: {} },
    uiSchema: { type: Object, default: {} },
    className: { type: String, default: '' },
    method: { type: String, default: 'POST' },
    url: { type: String, default: '' },
    turboFrame: { type: String, default: '' },
    scope: { type: String, default: '' },
    submitText: { type: String, default: 'Submit' }
  };

  static targets = ['container'];

  connect() {
    this.mountReactComponent();
  }

  disconnect() {
    this.unmountReactComponent();
  }

  mountReactComponent() {
    if (!this.hasContainerTarget) {
      console.error('JsonSchemaForm: No container target found');
      return;
    }

    // Use provided UI schema or generate defaults
    let finalUiSchema = this.uiSchemaValue && Object.keys(this.uiSchemaValue).length > 0
      ? this.uiSchemaValue
      : DefaultUiSchemaBuilder.build(this.schemaValue);

    // Add submit button options if submitText is provided
    if (this.submitTextValue && this.submitTextValue !== 'Submit') {
      finalUiSchema = {
        ...finalUiSchema,
        'ui:submitButtonOptions': {
          submitText: this.submitTextValue
        }
      };
    }

    this.reactRoot = createRoot(this.containerTarget);

    this.reactRoot.render(
      React.createElement(JsonSchemaForm, {
        schema: this.schemaValue,
        formData: this.formDataValue,
        uiSchema: finalUiSchema,
        className: this.classNameValue,
        onSubmit: this.handleSubmit.bind(this),
        onChange: this.handleChange.bind(this),
        onError: this.handleError.bind(this)
      })
    );
  }

  unmountReactComponent() {
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }
  }

  async handleSubmit(event) {
    // Dispatch a custom event that can be listened to by other Stimulus controllers
    this.dispatch('submit', { detail: event.formData });

    // If URL is provided, handle the submission automatically
    if (this.urlValue) {
      await this.submitForm(event.formData);
    }
  }

  async submitForm(formData) {
    try {
      const structuredData = this.structureFormData(formData);

      await HiddenFormBuilder.submitForm({
        url: this.urlValue,
        method: this.methodValue,
        turboFrame: this.turboFrameValue,
        formData: structuredData
      });

    } catch (error) {
      console.error('Form submission error:', error);
      this.dispatch('error', { detail: { message: 'Submission failed', error: error.message } });
    }
  }

  structureFormData(formData) {
    if (this.scopeValue) {
      const structured = {};
      structured[this.scopeValue] = formData;
      return structured;
    }
    return formData;
  }

  handleChange(event) {
    // Dispatch a custom event for form changes
    this.dispatch('change', { detail: event.formData });
  }

  handleError(errors) {
    // Dispatch a custom event for form errors
    this.dispatch('error', { detail: errors });
  }

  // Method to update form data from outside
  updateFormData(newData) {
    this.formDataValue = { ...this.formDataValue, ...newData };
    this.mountReactComponent(); // Re-render with new data
  }

  // Method to manually submit the form
  submit() {
    if (this.reactRoot) {
      // Trigger form submission through React component
      const formElement = this.containerTarget.querySelector('form');
      if (formElement) {
        formElement.requestSubmit();
      }
    }
  }
}
